import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDocs } from 'hooks/useDocs';
import { useTitle } from 'react-use';
import { Quote } from 'shared/types/quote';
import { AsyncTaskState } from 'library/async';
import { toast } from 'material-react-toastify';
import { Navigator } from 'shared/components/Navigator';
import { NavigatorLayout } from './NavigatorLayout';
import { loadTableStrings, routes } from 'shared/constants';
import { useHistory } from 'react-router-dom';
import { Button, Card, Typography, LinearProgress, TextField, MenuItem, SxProps, Theme, Box } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { QuoteTable } from 'components/ListQuotes/QuoteTable';
import { QueryPayloadOverride as QueryPayload } from '@insight2profit/api-connector/dist/services';
import { useDebounce } from 'hooks/use-debounce';

const containerStyles: SxProps<Theme> = {
  width: '100%',
  flex: '1 1 auto',
  margin: '15px 0px 15px 0px',
  padding: '15px',
  display: 'flex',
  flexDirection: 'column',
};
const centeredMessageStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '90%',
};
const tableCardStyles: SxProps<Theme> = { overflow: 'auto' };
const tableCardFooterStyles: SxProps<Theme> = {
  marginTop: 'auto',
  paddingTop: '15px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
};
const selectStyles: SxProps<Theme> = { width: 150 };

const initialFilter: QueryPayload = {
  document: {
    id: [
      {
        operator: 'contains',
        value: '',
      },
    ],
  },
  sortBy: 'createDate',
  sortOrder: 'desc',
  pageSize: 10,
  page: 0,
};
export const ListQuotes = () => {
  useTitle('Quotes');

  const { getDocs, getDocsTask } = useDocs<Required<Quote>>({
    clientId: 'demo-plq',
    documentType: 'quote',
  });

  const [quotes, setQuotes] = useState<Required<Quote>[]>([]);
  const [filters, setFilters] = useState<QueryPayload>(initialFilter);
  const history = useHistory();

  const debouncedFilters = useDebounce(filters, 900);

  useEffect(() => {
    getDocs(debouncedFilters)
      .then(res => {
        setQuotes(res.data);
        if (!res.data.length) {
          toast.warning(`No quotes found.`);
        }
      })
      .catch(error => toast.error('Something went wrong.'));
  }, [getDocs, setQuotes, debouncedFilters]);

  const fromResult = useMemo(() => {
    if (getDocsTask.taskState !== AsyncTaskState.COMPLETED) return 0;
    const { currentPage, pageSize } = getDocsTask.result.metadata;
    return currentPage * pageSize + 1;
  }, [getDocsTask]);

  const toResult = useMemo(() => {
    if (getDocsTask.taskState !== AsyncTaskState.COMPLETED) return 0;
    const { currentPage, pageSize, totalCount } = getDocsTask.result.metadata;
    const currentPageMaxPossible = (currentPage + 1) * pageSize; // on all pages except the last page
    return currentPageMaxPossible > totalCount ? totalCount : currentPageMaxPossible;
  }, [getDocsTask]);

  const handleSingleFilterChanged = useCallback(
    (newFilter: { [key: string]: string }) => {
      const key = Object.keys(newFilter) ? Object.keys(newFilter)[0] : undefined;
      if (!key || typeof newFilter[key] !== 'string') return;
      setFilters(state => {
        const newState = { ...state };
        if (typeof newState.document[key] !== 'undefined' && newFilter[key] === '') {
          //cleared this field
          delete newState.document[key];
          return newState;
        } else {
          const value = key === 'id' || key === 'revenue' ? newFilter[key] : newFilter[key].toString().toUpperCase();
          return {
            ...state,
            document: {
              ...state.document,
              [key]: [
                {
                  operator: 'contains',
                  value,
                },
              ],
            },
          };
        }
      });
    },
    [setFilters]
  );

  const handleClearAllFilters = useCallback(() => setFilters(initialFilter), [setFilters]);

  return (
    <>
      <NavigatorLayout>
        <Navigator last='Quotes' />
        <Button variant='contained' color='primary' onClick={() => history.push(routes.createQuote)}>
          New Quote
        </Button>
      </NavigatorLayout>
      <Card sx={containerStyles}>
        <Box sx={tableCardStyles}>
          <QuoteTable
            filters={filters}
            handleSingleFilterChanged={handleSingleFilterChanged}
            handleClearAllFilters={handleClearAllFilters}
            quotes={[...quotes]}
          />
        </Box>
        {getDocsTask.taskState === AsyncTaskState.COMPLETED && quotes.length > 0 && (
          <Box sx={tableCardFooterStyles}>
            {`Showing ${fromResult} - ${toResult} of ${getDocsTask.result.metadata.totalCount}`}
            <Pagination
              color='secondary'
              count={getDocsTask.result.metadata.totalPages}
              page={getDocsTask.result.metadata.currentPage + 1} // 0 indexed
              onChange={(_, page) => {
                setFilters(state => ({
                  ...state,
                  page: page - 1, // 0 indexed
                }));
              }}
            />
            <TextField
              sx={selectStyles}
              select
              variant='outlined'
              label='Per Page'
              value={filters.pageSize}
              onChange={e =>
                setFilters(state => ({
                  ...state,
                  pageSize: e.target.value,
                  page: 0,
                }))
              }
            >
              {[10, 25, 50, 100].map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {getDocsTask.taskState === AsyncTaskState.IN_PROGRESS && <LinearProgress color='secondary' />}
        {(!quotes || !quotes.length) && (
          <Box sx={centeredMessageStyles}>
            <Typography variant='h6'>{loadTableStrings[getDocsTask.taskState]}</Typography>
          </Box>
        )}
      </Card>
    </>
  );
};
